import React, { useEffect, useState } from "react";
import GenericPopup from "@Components/Popup/Popup";
import Joi from "joi";
import {
  OptionalDropDownValidation,
  WithoutValidation,
} from "@Utils/Validations/UserValidations/UserValidationFieldsSchema";
import { validateForm } from "@Utils/Validations/HelperFunctions/FormValidationFunctions";
import { Grid, TextField, Typography } from "@mui/material";
import DateSelector from "@Components/DateTimePickers/DatePicker";
import LockOpenRoundedIcon from "@mui/icons-material/LockOpenRounded";
import { IBDValidation } from "@Utils/Validations/UserValidations/UserValidationFieldsSchema";
import SearchableDropDown from "../../Components/DropDown/SearchableDropDown";
import { WAREHOUSES_URL } from "../../API/APIRoutes";
import useGetManyRequests from "../../Hooks/useGetManyRequests";
export const ReleaseOrderPopup = (
  showPopup,
  closePopup,
  Confirm,
  loading,
  notesForReleaseOrder
) => {
  const emptyRelease = {
    releaseDate: null,
    warehouseId: "",
    warehouseArrivalDate: null,
    ibdNumber: "",
    ibdDate: null,
  };
  const [release, setRelease] = useState({
    ...emptyRelease,
  });
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const optionsMapping = [
    {
      index: 0,
      key: "warehouseName",
      id: "warehouseId",
      setter: setWarehouseOptions,
    },
  ];

  const { loading: warehousesLoading, getAll } = useGetManyRequests();
  let urls = [WAREHOUSES_URL];

  useEffect(() => {
    if (showPopup) {
      getAll(urls).then((res) => {
        optionsMapping.forEach(({ index, key, id, setter }) => {
          setter(
            res[index]?.map((item) => ({
              name: item[key],
              id: String(item[id]),
            }))
          );
        });
      });
    }
  }, [showPopup]);

  const validationSchema = Joi.object({
    releaseDate: WithoutValidation,
    warehouseId: OptionalDropDownValidation("Warehouse", warehouseOptions),
    ibdNumber: IBDValidation,
    ibdDate: WithoutValidation,
    warehouseArrivalDate: WithoutValidation,
  });

  const [errors, setErrors] = useState({
    releaseDate: "",
    warehouseId: "",
    warehouseArrivalDate: "",
    ibdNumber: "",
    ibdDate: "",
  });

  const onReleaseDatePick = (newValue) => {
    setRelease({ ...release, releaseDate: newValue });
    setErrors({ ...errors, releaseDate: "" });
  };
  const onWarehouseArrivalDatePick = (newValue) => {
    setRelease({ ...release, warehouseArrivalDate: newValue });
    setErrors({ ...errors, warehouseArrivalDate: "" });
  };
  const onIbdDatePick = (newValue) => {
    setRelease({ ...release, ibdDate: newValue });
    setErrors({ ...errors, ibdDate: "" });
  };

  const content = (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <DateSelector
          name={"Release Date"}
          value={release.releaseDate}
          error={errors.releaseDate}
          onPick={onReleaseDatePick}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body1" sx={{ mb: 1 }}>
          Warehouse
        </Typography>
        <SearchableDropDown
          state={release.warehouseId}
          setState={(value) => {
            setRelease({ ...release, warehouseId: value });
            setErrors({ ...errors, warehouseId: "" });
          }}
          name="warehouseId"
          label=""
          items={warehouseOptions}
          placeholder="Select a Warehouse"
          helperText={errors.warehouseId}
          loading={warehousesLoading}
        />
      </Grid>
      <Grid item xs={12}>
        <DateSelector
          name={"Warehouse Arrival Date"}
          value={release.warehouseArrivalDate}
          error={errors.warehouseArrivalDate}
          onPick={onWarehouseArrivalDatePick}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body1" sx={{ mb: 1 }}>
          IBD Number
        </Typography>
        <TextField
          InputLabelProps={{
            shrink: true,
          }}
          variant="outlined"
          placeholder={"Enter IBD Number (Optional)"}
          value={release.ibdNumber}
          onChange={(e) => {
            setRelease({ ...release, ibdNumber: e.target.value });
            setErrors({ ...errors, ibdNumber: "" });
          }}
          fullWidth
          helperText={
            <Typography variant="body2" marginTop={1} color={"error"}>
              {errors.ibdNumber || ""}
            </Typography>
          }
        />
      </Grid>
      <Grid item xs={12}>
        <DateSelector
          name={"IBD Date"}
          value={release.ibdDate}
          error={errors.ibdDate}
          onPick={onIbdDatePick}
        />
      </Grid>
    </Grid>
  );

  const postReleaseCallBack = () => {
    setRelease({ ...emptyRelease });
    setErrors({
      releaseDate: "",
      warehouseId: "",
      warehouseArrivalDate: "",
      ibdNumber: "",
      ibdDate: "",
    });
  };

  const submit = () => {
    const selectedWarehouse = warehouseOptions.find(
      (wh) => wh.id === release.warehouseId
    );

    const formattedRelease = {
      ...release,
      releaseDate: release.releaseDate ? new Date(release.releaseDate) : null,
      warehouseArrivalDate: release.warehouseArrivalDate
        ? new Date(release.warehouseArrivalDate)
        : null,
      ibdDate: release.ibdDate ? new Date(release.ibdDate) : null,
      ibdNumber: release.ibdNumber,
    };

    if (validateForm(formattedRelease, setErrors, validationSchema)) {
      const sentRelease = {
        ...formattedRelease,
        warehouse: selectedWarehouse
          ? {
              warehouseId: selectedWarehouse.id,
              warehouseName: selectedWarehouse.name,
            }
          : null,
      };
      delete sentRelease.warehouseId; // Remove the warehouseId field
      Confirm(sentRelease);
      postReleaseCallBack();
    }
  };

  return (
    <GenericPopup
      icon={<LockOpenRoundedIcon color="white" fontSize="large" />}
      open={showPopup}
      title={`Release Order`}
      content={content}
      buttons={[
        {
          text: "Cancel",
          onClick: closePopup,
          color: "secondary",
          variant: "outlined",
        },
        {
          text: "Release",
          onClick: submit,
          color: "primary",
          variant: "contained",
          loading: loading,
        },
      ]}
      noteData={notesForReleaseOrder}
    />
  );
};
